<template>
  <div class="border-container-unique38 mb-4">
    <div class="admin-content-layout">
      <div
        class="admin-main-content"
        style="padding-top: 0px; max-width: 100%; padding-right: 0px"
      >
        <!-- Header Section -->
        <div class="d-flex justify-space-between align-items-center mb-4">
          <div class="admin-header">
            <h1>
              <img
                src="/images/icon/cart.svg"
                class="filter-primary"
                height="20"
                alt=""
              />
              Checkout Sequence
            </h1>
          </div>
        </div>

        <!-- Filter Buttons Section -->
        <div class="d-flex justify-space-between align-items-center mb-4">
          <div class="d-flex ga-3">
            <SingleSelect
              v-model="selectedDate"
              :options="dateOptions"
              prefix="Date: "
              placeholder="Select date range"
            />
            <SingleSelect
              v-model="selectedRegion"
              :options="regionOptions"
              prefix="Region: "
              placeholder="Select region"
            />
            <SingleSelect
              v-model="selectedUserType"
              :options="userTypeOptions"
              prefix="User type: "
              placeholder="Select user type"
            />
          </div>
          <ActionButton
            variant="tertiary"
            size="medium"
            :loading="exportLoading"
            loading-text="Exporting..."
            @click="handleExportCSV"
          >
            Export CSV.
          </ActionButton>
        </div>

        <!-- Custom Date Range Picker -->
        <div
          v-if="selectedDate === 'custom'"
          class="mb-4"
          style="max-width: 450px"
        >
          <DateRangePicker
            v-model="customDateRange"
            @apply="handleCustomDateApply"
          />
        </div>

        <!-- Funnel Tracking Section -->
        <div class="funnel-tracking-container mb-4">
          <h2 class="funnel-title">Funnel Tracking</h2>

          <!-- Metrics Cards -->
          <div class="metrics-row mb-4">
            <div class="metric-card">
              <div class="metric-label">End-to-end conversion</div>
              <div class="metric-value">{{ endToEndConversion }}%</div>
              <div class="metric-description">From step 1 to step 3</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Total quotes</div>
              <div class="metric-value">{{ totalQuotes }}</div>
              <div class="metric-description">
                All quotes that has been generated
              </div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Highest drop-off</div>
              <div class="metric-value error">{{ highestDropoff.value }}%</div>
              <div class="metric-description">
                From step {{ highestDropoff.step }}
              </div>
            </div>
          </div>

          <!-- Funnel Steps Visualization -->
          <div class="funnel-steps">
            <div class="funnel-step">
              <div class="step-header">
                <div class="step-title">Step 1: View/edit</div>
                <div class="step-percentage">
                  {{ funnelSteps.step1.percentage }}%
                </div>
              </div>
              <div class="step-chart">
                <div
                  class="chart-bar"
                  :class="
                    funnelSteps.step1.percentage === 100
                      ? 'full-bar'
                      : 'partial-bar'
                  "
                  :style="{ height: funnelSteps.step1.percentage + '%' }"
                ></div>
              </div>
              <div class="step-users">{{ funnelSteps.step1.count }} users</div>
            </div>
            <div class="funnel-step">
              <div class="step-header">
                <div class="step-title">
                  Step 2: Login/Signup/Guest Selection
                </div>
                <div class="step-percentage">
                  {{ funnelSteps.step2.percentage }}%
                </div>
                <div v-if="funnelSteps.step2.dropoff > 0" class="dropoff-text">
                  (-{{ funnelSteps.step2.dropoff }}% drop-off)
                </div>
              </div>
              <div class="step-chart">
                <div
                  class="chart-bar partial-bar"
                  :style="{ height: funnelSteps.step2.percentage + '%' }"
                ></div>
              </div>
              <div class="step-users">
                <div>{{ funnelSteps.step2.count }} users</div>
              </div>
            </div>
            <div class="funnel-step">
              <div class="step-header">
                <div class="step-title">Step 3: Quote generated</div>
                <div class="step-percentage">
                  {{ funnelSteps.step3.percentage }}%
                </div>
                <div v-if="funnelSteps.step3.dropoff > 0" class="dropoff-text">
                  (-{{ funnelSteps.step3.dropoff }}% drop-off)
                </div>
              </div>
              <div class="step-chart">
                <div
                  class="chart-bar partial-bar"
                  :style="{ height: funnelSteps.step3.percentage + '%' }"
                ></div>
              </div>
              <div class="step-users">
                <div>{{ funnelSteps.step3.count }} users</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Quote Generated Section -->
        <div class="quote-generated-container mb-4">
          <div class="quote-header">
            <h2 class="quote-title">
              Total: {{ totalQuotesGenerated }} Quote Generated
            </h2>
            <p class="quote-subtitle">The currency will follow the region</p>
          </div>

          <div class="price-ranges">
            <div
              v-for="range in quoteRanges"
              :key="range.label"
              class="price-range-item"
            >
              <div class="range-label">{{ range.label }}</div>
              <div class="range-chart">
                <div
                  class="range-bar"
                  :style="{ width: range.percentage + '%' }"
                ></div>
              </div>
              <div class="range-count">
                {{ range.count }} Quote{{ range.count !== 1 ? "s" : "" }}
              </div>
            </div>
          </div>
        </div>

        <div class="recent-sessions-container">
          <h2 class="sessions-title">Recent Sessions</h2>

          <TableData
            :headers="tableHeaders"
            :items="recentSessions"
            :loading="loading"
            :total-pages="totalPages"
            :current-page="currentPage"
            :show-data-info="true"
            :total-rows="cartLogsData?.total_rows || 0"
            :current-limit="itemsPerPage"
            @update:current-page="updatePage"
            @sort="handleSort"
            variant="rounded"
          >
            <template #cell-status="{ value }">
              <span
                class="status-badge"
                :class="
                  value === 'Completed' ? 'status-completed' : 'status-dropoff'
                "
              >
                {{ value }}
              </span>
            </template>
          </TableData>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, watch, onMounted } from "vue";
import SingleSelect from "@/components/common/SingleSelect.vue";
import TableData from "@/components/common/TableData.vue";
import ActionButton from "@/components/common/ActionButton.vue";
import DateRangePicker from "@/components/common/DateRangePicker.vue";
import makercartLogService from "@/services/makercartLogService";

const dateOptions = [
  { value: "all", text: "<strong>All Dates</strong>" },
  { value: "daily", text: "<strong>Daily</strong>" },
  { value: "weekly", text: "<strong>Weekly</strong>" },
  { value: "monthly", text: "<strong>Monthly</strong>" },
  { value: "custom", text: "<strong>Custom Date</strong>" },
];

const regionOptions = [
  { value: "all", text: "<strong>All Regions</strong>" },
  { value: "US", text: "<strong>US</strong>" },
  { value: "SG", text: "<strong>SG</strong>" },
];

const userTypeOptions = [
  { value: "all", text: "<strong>All User State</strong>" },
  { value: "Guest", text: "<strong>Guest</strong>" },
  { value: "Logged-in", text: "<strong>Logged-in</strong>" },
];

const selectedDate = ref("all");
const selectedRegion = ref("all");
const selectedUserType = ref("all");
const customDateRange = ref({ from: null, to: null });

const sessionCountData = ref(null);
const downloadedQuoteData = ref(null);
const cartLogsData = ref(null);
const loading = ref(false);
const exportLoading = ref(false);

const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalPages = ref(1);

const tableHeaders = [
  { text: "User/email", value: "email", sortable: true },
  { text: "User state", value: "userState", sortable: true },
  { text: "Time spent (1)", value: "timeSpent1", sortable: true },
  { text: "Time spent (2)", value: "timeSpent2", sortable: true },
  { text: "Status", value: "status", sortable: true, centerBody: true },
  { text: "Quote ID", value: "quoteId", sortable: true },
  { text: "Total", value: "total", sortable: true },
];

const funnelSteps = computed(() => {
  if (!sessionCountData.value) {
    return {
      step1: { percentage: 0, count: 0, dropoff: 0 },
      step2: { percentage: 0, count: 0, dropoff: 0 },
      step3: { percentage: 0, count: 0, dropoff: 0 },
    };
  }

  const data = sessionCountData.value;
  return {
    step1: {
      percentage: data["step-1"]?.["percent-completed"] || 100,
      count: data["step-1"]?.count || 0,
      dropoff: data["step-1"]?.["percent-drop-off"] || 0,
    },
    step2: {
      percentage: data["step-2"]?.["percent-completed"] || 0,
      count: data["step-2"]?.count || 0,
      dropoff: data["step-2"]?.["percent-drop-off"] || 0,
    },
    step3: {
      percentage: data["step-3"]?.["percent-completed"] || 0,
      count: data["step-3"]?.count || 0,
      dropoff: data["step-3"]?.["percent-drop-off"] || 0,
    },
  };
});

const endToEndConversion = computed(() => {
  if (!sessionCountData.value) return 0;
  return funnelSteps.value.step3.percentage;
});

const totalQuotes = computed(() => {
  if (!sessionCountData.value) return 0;
  return funnelSteps.value.step3.count;
});

const highestDropoff = computed(() => {
  if (!sessionCountData.value) return { value: 0, step: "1 to 2" };
  const dropoffs = [
    { value: funnelSteps.value.step1.dropoff, step: "1 to 2" },
    { value: funnelSteps.value.step2.dropoff, step: "2 to 3" },
  ];
  return dropoffs.reduce((max, current) =>
    current.value > max.value ? current : max
  );
});

const quoteRanges = computed(() => {
  if (!downloadedQuoteData.value) {
    return [
      { label: "0-1 K", count: 0, percentage: 0 },
      { label: "1-3 K", count: 0, percentage: 0 },
      { label: "3-5 K", count: 0, percentage: 0 },
      { label: "5-10 K", count: 0, percentage: 0 },
      { label: "10 K +", count: 0, percentage: 0 },
    ];
  }

  const data = downloadedQuoteData.value;
  const total = Math.max(
    data["0-1K"] + data["1-3K"] + data["3-5K"] + data["5-10K"] + data["10K+"],
    1
  );

  return [
    {
      label: "0-1 K",
      count: data["0-1K"] || 0,
      percentage: ((data["0-1K"] || 0) / total) * 100,
    },
    {
      label: "1-3 K",
      count: data["1-3K"] || 0,
      percentage: ((data["1-3K"] || 0) / total) * 100,
    },
    {
      label: "3-5 K",
      count: data["3-5K"] || 0,
      percentage: ((data["3-5K"] || 0) / total) * 100,
    },
    {
      label: "5-10 K",
      count: data["5-10K"] || 0,
      percentage: ((data["5-10K"] || 0) / total) * 100,
    },
    {
      label: "10 K +",
      count: data["10K+"] || 0,
      percentage: ((data["10K+"] || 0) / total) * 100,
    },
  ];
});

const totalQuotesGenerated = computed(() => {
  if (!downloadedQuoteData.value) return 0;
  const data = downloadedQuoteData.value;
  return (
    (data["0-1K"] || 0) +
    (data["1-3K"] || 0) +
    (data["3-5K"] || 0) +
    (data["5-10K"] || 0) +
    (data["10K+"] || 0)
  );
});

const formatTimeSpent = (seconds) => {
  if (!seconds && seconds !== 0) return "-";
  if (seconds < 60) return `${seconds} sec`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} min`;
};

const formatCurrency = (amount, region) => {
  if (!amount && amount !== 0) return "-";
  const currencySymbols = {
    US: "$",
    SG: "$",
    JP: "¥",
  };
  const symbol = currencySymbols[region?.toUpperCase()] || "$";
  return `${symbol}${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const recentSessions = computed(() => {
  if (!cartLogsData.value || !cartLogsData.value.rows) return [];

  return cartLogsData.value.rows.map((log) => ({
    id: log.id,
    email: log.email || "-",
    userState: log.user_state || "Guest",
    timeSpent1: formatTimeSpent(log.time_spent_1),
    timeSpent2: formatTimeSpent(log.time_spent_2),
    timeSpent3: formatTimeSpent(log.time_spent_3),
    status: log.status || "-",
    quoteId: log.quotes_code || "-",
    total: formatCurrency(log.quotes_total, log.region),
  }));
});

const getDateRange = () => {
  const now = new Date();
  let to = now.toISOString().split("T")[0];
  let from;

  switch (selectedDate.value) {
    case "daily":
      from = to;
      to = null;
      break;
    case "weekly": {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      from = weekAgo.toISOString().split("T")[0];
      break;
    }
    case "monthly": {
      const monthAgo = new Date(now);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      from = monthAgo.toISOString().split("T")[0];
      break;
    }
    case "custom":
      if (customDateRange.value.from && customDateRange.value.to) {
        from = customDateRange.value.from;
        to = customDateRange.value.to;
      } else {
        // If custom is selected but no dates chosen, return null to not filter
        return { from: null, to: null };
      }
      break;
    default:
      from = to;
  }

  return { from, to };
};

const buildRequestFilters = () => {
  const filters = {};
  if (selectedUserType.value !== "all")
    filters.user_state = selectedUserType.value;
  if (selectedRegion.value !== "all")
    filters.region = selectedRegion.value.toUpperCase();

  if (selectedDate.value !== "all") {
    const { from, to } = getDateRange();
    if (from) filters.from = from;
    if (to) filters.to = to;
  }

  return filters;
};

const fetchSessionCount = async (filters) => {
  try {
    loading.value = true;
    const response = await makercartLogService.getSessionCount({
      page: 1,
      limit: 10,
      sort_by: "created_at",
      order: "desc",
      ...filters,
    });
    if (response.success) {
      sessionCountData.value = response.data;
    }
  } catch (error) {
    console.error("Error fetching session count:", error);
  } finally {
    loading.value = false;
  }
};

const fetchDownloadedQuote = async (filters) => {
  try {
    loading.value = true;
    const response = await makercartLogService.getDownloadedQuote({
      ...filters,
    });
    if (response.success) {
      downloadedQuoteData.value = response.data;
    }
  } catch (error) {
    console.error("Error fetching downloaded quote:", error);
  } finally {
    loading.value = false;
  }
};

const fetchCartLogs = async (filters) => {
  try {
    loading.value = true;
    const response = await makercartLogService.getCartLogs({
      page: currentPage.value,
      limit: itemsPerPage.value,
      sort_by: "created_at",
      order: "desc",
      ...filters,
    });
    if (response.success) {
      cartLogsData.value = response.data;
      totalPages.value = response.data.total_page || 1;
    }
  } catch (error) {
    console.error("Error fetching cart logs:", error);
  } finally {
    loading.value = false;
  }
};

const fetchAllData = async () => {
  const filters = buildRequestFilters();
  await Promise.all([
    fetchSessionCount(filters),
    fetchDownloadedQuote(filters),
    fetchCartLogs(filters),
  ]);
};

const updatePage = (page) => {
  currentPage.value = page;
  fetchCartLogs(buildRequestFilters());
};

const handleSort = (sortData) => {
  console.log("Sort data:", sortData);
};

const handleCustomDateApply = (dateRange) => {
  customDateRange.value = dateRange;
  currentPage.value = 1;
  fetchAllData();
};

const handleExportCSV = async () => {
  try {
    exportLoading.value = true;
    const filters = buildRequestFilters();

    const blob = await makercartLogService.exportCartLogs(filters);

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    const now = new Date();
    const dateStr = now.toISOString().split("T")[0];
    const regionStr =
      selectedRegion.value !== "all" ? `_${selectedRegion.value}` : "";
    const userTypeStr =
      selectedUserType.value !== "all" ? `_${selectedUserType.value}` : "";
    const dateRangeStr =
      selectedDate.value !== "daily" ? `_${selectedDate.value}` : "";

    link.download = `cart_logs_${dateStr}${regionStr}${userTypeStr}${dateRangeStr}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error exporting CSV:", error);
  } finally {
    exportLoading.value = false;
  }
};

watch([selectedDate, selectedRegion, selectedUserType], () => {
  // Reset custom date range if user switches away from custom
  if (selectedDate.value !== "custom") {
    customDateRange.value = { from: null, to: null };
  }
  currentPage.value = 1;
  fetchAllData();
});

onMounted(() => {
  fetchAllData();
});
</script>

<style scoped>
/* Filter Buttons */
.filter-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid var(--primary-blue);
  border-radius: 12px;
  background: white;
  cursor: pointer;
}

.filter-label {
  font-size: var(--fs-big);
  font-weight: 400;
  color: var(--primary-blue);
}

.filter-value {
  font-size: var(--fs-medium);
  font-weight: 700;
  color: var(--primary-blue);
}

.filter-icon {
  width: 16px;
  height: 9px;
}

/* Funnel Tracking */
.funnel-tracking-container {
  background: #f2f3fb;
  padding: 32px;
  border-radius: 16px;
  box-shadow:
    0px 1px 3px 0px rgba(16, 24, 40, 0.1),
    0px 1px 2px 0px rgba(16, 24, 40, 0.06);
}

.funnel-title {
  font-size: 36px;
  font-weight: 700;
  color: var(--primary-blue);
  margin-bottom: 32px;
}

.metrics-row {
  display: flex;
  gap: 12px;
}

.metric-card {
  flex: 1;
  background: white;
  padding: 12px;
  border-radius: 12px;
  box-shadow:
    0px 6px 10px 4px rgba(0, 0, 0, 0.15),
    0px 2px 3px 0px rgba(0, 0, 0, 0.1);
}

.metric-label {
  font-size: var(--fs-medium);
  font-weight: 400;
  color: #98a2b3;
  margin-bottom: 8px;
}

.metric-value {
  font-size: 32px;
  font-weight: 700;
  color: var(--primary-blue);
  margin-bottom: 8px;
}

.metric-value.error {
  color: #c21d1d;
}

.metric-description {
  font-size: var(--fs-medium);
  font-weight: 500;
  color: #3b4648;
}

.funnel-steps {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

.funnel-step {
  flex: 1;
  background: white;
  padding: 12px;
  border-radius: 12px;
  box-shadow:
    0px 6px 10px 4px rgba(0, 0, 0, 0.15),
    0px 2px 3px 0px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 300px;
}

.step-header {
  margin-bottom: 8px;
}

.step-title {
  font-size: var(--fs-medium);
  font-weight: 400;
  color: #98a2b3;
  margin-bottom: 8px;
}

.step-percentage {
  font-size: 32px;
  font-weight: 700;
  color: var(--primary-blue);
  text-align: center;
}

.step-chart {
  flex: 1;
  display: flex;
  align-items: end;
  margin: 8px 0;
}

.chart-bar {
  width: 100%;
  border-radius: 12px;
}

.full-bar {
  height: 100%;
  background: #0478ce;
}

.partial-bar {
  background: #0478ce;
  border-radius: 0 0 12px 12px;
}

.step-users {
  text-align: center;
  font-size: var(--fs-medium);
  font-weight: 500;
  color: #3b4648;
}

.dropoff-text {
  color: #c21d1d;
  font-size: var(--fs-medium);
  text-align: center;
  font-weight: 500;
}

/* Quote Generated */
.quote-generated-container {
  background: #f2f3fb;
  padding: 32px;
  border-radius: 16px;
  box-shadow:
    0px 1px 3px 0px rgba(16, 24, 40, 0.1),
    0px 1px 2px 0px rgba(16, 24, 40, 0.06);
}

.quote-header {
  margin-bottom: 32px;
}

.quote-title {
  margin-top: 0px;
  font-size: 36px;
  font-weight: 700;
  color: var(--primary-blue);
  margin-bottom: 4px;
}

.quote-subtitle {
  font-size: var(--fs-medium);
  font-weight: 400;
  color: #6c737f;
}

.price-ranges {
  background: white;
  padding: 12px;
  border-radius: 12px;
  box-shadow:
    0px 6px 10px 4px rgba(0, 0, 0, 0.15),
    0px 2px 3px 0px rgba(0, 0, 0, 0.1);
}

.price-range-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.range-label {
  font-size: var(--fs-large);
  font-weight: 400;
  color: var(--primary-blue);
  width: 80px;
}

.range-chart {
  flex: 1;
  height: 30px;
  background: #dfe2e7;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}

.range-bar {
  height: 100%;
  background: var(--primary-blue);
  border-radius: 12px;
  transition: width 0.3s ease;
}

.range-count {
  font-size: var(--fs-large);
  font-weight: 400;
  color: var(--primary-blue);
  text-align: right;
  width: 160px;
}

/* Recent Sessions */
.recent-sessions-container {
  background: #f2f3fb;
  padding: 32px;
  border-radius: 16px;
  box-shadow:
    0px 1px 3px 0px rgba(16, 24, 40, 0.1),
    0px 1px 2px 0px rgba(16, 24, 40, 0.06);
}

.sessions-title {
  font-size: 36px;
  font-weight: 700;
  color: var(--primary-blue);
  margin-bottom: 32px;
  margin-top: 0px;
}

.status-badge {
  display: inline-block;
  padding: 10px 16px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 400;
  color: white;
}

.status-completed {
  background: #56b693;
}

.status-dropoff {
  background: #d66868;
}

/* Responsive Design */
@media (max-width: 768px) {
  .metrics-row {
    flex-direction: column;
  }

  .funnel-steps {
    flex-direction: column;
  }

  .filter-button {
    flex-direction: column;
    text-align: center;
  }

  .price-range-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .range-chart {
    width: 100%;
  }

  .range-count {
    text-align: left;
    width: auto;
  }
}
</style>
